export enum UserRole {
  ADMIN = 1,
  SECRETARIA = 2,
  CORRETOR = 3
}

export const AVAILABLE_PERMISSIONS = [
  'incluir',
  'consultar',
  'editar',
  'publicar',
  'excluir'
] as const;

export type Permission = (typeof AVAILABLE_PERMISSIONS)[number];

export interface IRole {
  id: number;
  name: string;
  active: boolean;
  protected: boolean;
  permissions: Permission[];
  created_at: string;
  updated_at: string;
}

export interface IUser {
  id: number;
  name: string;
  avatar?: string | null;
  email: string;
  active: boolean;
  role_id: number;
  role?: IRole;
  creci?: string | null;
  cellphone?: string | null;
  office_phone?: string | null;
  pref_descricao_height?: string | null;
  pref_info_adicional_height?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  role_id: number;
  avatar?: string;
  creci?: string;
  cellphone?: string;
  office_phone?: string;
}

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  password?: string;
  role_id?: number;
  active?: boolean;
  avatar?: string;
  creci?: string;
  cellphone?: string;
  office_phone?: string;
}

export interface CreateRolePayload {
  name: string;
  permissions: Permission[];
  active?: boolean;
}

export interface UpdateRolePayload {
  name?: string;
  permissions?: Permission[];
  active?: boolean;
}

export interface UserState {
  user: IUser | null;
  role: UserRole | null;
  token: string | null;
  remember: boolean;
  adminUsers: IUser[];
  corretores: IUser[];
  roles: IRole[];

  logIn: (
    email: string,
    password: string,
    remember: boolean
  ) => Promise<boolean>;
  logOut: () => void;
  updateUserAvatar: (file: File) => Promise<boolean>;
  uploadUserAvatar: (file: File) => Promise<string | null>;

  // Admin – user management
  fetchUsers: () => Promise<void>;
  fetchCorretores: () => Promise<void>;
  createUser: (data: CreateUserPayload) => Promise<boolean>;
  updateUser: (id: number, data: UpdateUserPayload) => Promise<boolean>;
  toggleUser: (id: number) => Promise<boolean>;
  deleteUser: (id: number) => Promise<boolean>;

  // Auth – password recovery
  forgotPassword: (email: string) => Promise<boolean>;
  // Auth – change own password
  changePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<{ ok: boolean; message?: string }>;
  // Fetch own profile from server (refresh store with latest data)
  fetchMyProfile: () => Promise<void>;
  // Check server-side forced logout flag once on app bootstrap
  checkShouldLogoutOnBoot: () => Promise<boolean>;
  // Update own profile (cellphone)
  updateOwnProfile: (data: { cellphone?: string }) => Promise<boolean>;
  // Save editor height preferences
  saveEditorPreferences: (data: {
    pref_descricao_height?: string;
    pref_info_adicional_height?: string;
  }) => Promise<boolean>;

  // Admin – role management
  fetchRoles: () => Promise<void>;
  createRole: (data: CreateRolePayload) => Promise<boolean>;
  updateRole: (id: number, data: UpdateRolePayload) => Promise<boolean>;
  toggleRole: (id: number) => Promise<boolean>;
}
